import React, { useState, useEffect } from 'react';
import { PlanDetail } from '../../types';
import HostedCheckout from '../HostedCheckout';
import { parseCheckoutParams, resolvePlanFromParams, notifyConnectedWebsite } from '../../utils/checkoutUrlParams';
import { INITIAL_PLANS } from '../../services/mockData';
import { api } from '../../services/api';

interface ConnectedCheckoutHostProps {
  onClose: () => void;
}

const ConnectedCheckoutHost: React.FC<ConnectedCheckoutHostProps> = ({ onClose }) => {
  const [params] = useState(() => parseCheckoutParams());
  const [resolvedPlan, setResolvedPlan] = useState<PlanDetail>(INITIAL_PLANS[1]);
  const [isLoading, setIsLoading] = useState(true);
  const [checkoutIdentity, setCheckoutIdentity] = useState<Record<string, string>>({});

  useEffect(() => {
    let isMounted = true;
    notifyConnectedWebsite('SUBSCALE_READY', { planId: params.planId, tier: params.tier });

    async function initializeCheckout() {
      let sessionCustomer: Record<string, string> | undefined;
      let sessionPlan: PlanDetail | null = null;

      if (params.sessionId) {
        try {
          const session = await api.checkout.getSession(params.sessionId);
          if (session && isMounted) {
            if (session.plan) {
              sessionPlan = session.plan;
            }
            if (session.customer) {
              sessionCustomer = {
                name: session.customer.name || '',
                email: session.customer.email || '',
                phone: session.customer.phone || '',
                country: session.customer.country || '',
                gstin: session.customer.gstin || '',
              };
            }
          }
        } catch (err) {
          console.warn('Failed to fetch checkout session from server:', err);
        }
      }

      if (!sessionPlan) {
        try {
          sessionPlan = await resolvePlanFromParams(params);
        } catch (err) {
          console.warn('Failed to resolve plan from params:', err);
          sessionPlan = INITIAL_PLANS[1];
        }
      }

      const initialCustomer: Record<string, string> = {
        name: sessionCustomer?.name || params.name || '',
        email: sessionCustomer?.email || params.email || '',
        phone: sessionCustomer?.phone || params.phone || '',
        country: sessionCustomer?.country || params.country || '',
        gstin: sessionCustomer?.gstin || params.gstin || '',
      };

      if (isMounted) {
        if (sessionPlan) setResolvedPlan(sessionPlan);
        setCheckoutIdentity(initialCustomer);
        setIsLoading(false);
      }
    }

    initializeCheckout();

    return () => {
      isMounted = false;
    };
  }, [params]);

  const handleClose = () => {
    notifyConnectedWebsite('SUBSCALE_CHECKOUT_CANCEL', {
      planId: resolvedPlan.id,
      tier: resolvedPlan.tier,
    });

    if (params.cancelUrl) {
      window.location.href = params.cancelUrl;
      return;
    }

    onClose();
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center text-white space-y-4 p-6">
        <div className="w-10 h-10 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin"></div>
        <p className="text-sm font-bold text-slate-300">Initializing Secure Checkout...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950">
      <HostedCheckout
        plan={resolvedPlan}
        onClose={handleClose}
        initialFormDataOverride={checkoutIdentity}
        initialBillingCycle={params.billingCycle}
      />
    </div>
  );
};

export default ConnectedCheckoutHost;
